import type {
  ToolRow,
  ToolCell,
  TrolleySheetData,
} from '@/utils/tool-management/tool-management.util'
import {
  getDataRowsWithoutDescription,
  isDescriptionRow,
} from '@/utils/tool-management/tool-management.util'

import { CellEnum } from '@/enums/cell.enum'

export function isValidRow(row: ToolRow): boolean {
  const drawerValue = row[CellEnum.Drawer] as ToolCell
  const pnValue = row[CellEnum.Pn] as ToolCell
  const chineseNameValue = row[CellEnum.ChineseName] as ToolCell
  const descriptionValue = row[CellEnum.Description] as ToolCell
  const quantityValue = row[CellEnum.Quantity] as ToolCell

  const isDrawerEmpty = !drawerValue || drawerValue === ''
  const isPnEmpty = !pnValue || pnValue === ''
  const isChineseNameEmpty = !chineseNameValue || chineseNameValue === ''

  const isQuantityEmpty =
    quantityValue === null ||
    quantityValue === undefined ||
    quantityValue === ''

  const isQuantityNonNumeric = (() => {
    if (isQuantityEmpty) return false
    const numValue =
      typeof quantityValue === 'number' ? quantityValue : Number(quantityValue)
    return isNaN(numValue) || !isFinite(numValue)
  })()

  const isQuantityTooLarge = (() => {
    if (isQuantityEmpty || isQuantityNonNumeric) return false
    return String(quantityValue).length > 5
  })()

  const isDescriptionTooLong = (() => {
    if (!descriptionValue || descriptionValue === '') return false
    return String(descriptionValue).length > 100
  })()

  const isPnTooLong = (() => {
    if (!pnValue || pnValue === '') return false
    return String(pnValue).length > 20
  })()

  const isChineseNameTooLong = (() => {
    if (!chineseNameValue || chineseNameValue === '') return false
    return String(chineseNameValue).length > 100
  })()

  return !(
    isDrawerEmpty ||
    isPnEmpty ||
    isChineseNameEmpty ||
    isQuantityEmpty ||
    isQuantityNonNumeric ||
    isQuantityTooLarge ||
    isDescriptionTooLong ||
    isPnTooLong ||
    isChineseNameTooLong
  )
}

export function isValidImportRow(row: ToolRow): boolean {
  const drawerValue = row[CellEnum.Drawer] as ToolCell
  const pnValue = row[CellEnum.Pn] as ToolCell
  const chineseNameValue = row[CellEnum.ChineseName] as ToolCell
  const descriptionValue = row[CellEnum.Description] as ToolCell
  const quantityValue = row[CellEnum.Quantity] as ToolCell

  const isDrawerEmpty = !drawerValue || drawerValue === ''
  const isPnEmpty = !pnValue || pnValue === ''
  const isChineseNameEmpty = !chineseNameValue || chineseNameValue === ''

  const isQuantityEmpty =
    quantityValue === null ||
    quantityValue === undefined ||
    quantityValue === ''

  const isQuantityNonNumeric = (() => {
    if (isQuantityEmpty) return false
    const numValue =
      typeof quantityValue === 'number' ? quantityValue : Number(quantityValue)
    return isNaN(numValue) || !isFinite(numValue)
  })()

  const isQuantityTooLarge = (() => {
    if (isQuantityEmpty || isQuantityNonNumeric) return false
    return String(quantityValue).length > 5
  })()

  const isDescriptionTooLong = (() => {
    if (!descriptionValue || descriptionValue === '') return false
    return String(descriptionValue).length > 100
  })()

  const isPnTooLong = (() => {
    if (!pnValue || pnValue === '') return false
    return String(pnValue).length > 20
  })()

  const isChineseNameTooLong = (() => {
    if (!chineseNameValue || chineseNameValue === '') return false
    return String(chineseNameValue).length > 100
  })()

  return !(
    isDrawerEmpty ||
    isPnEmpty ||
    isChineseNameEmpty ||
    isQuantityEmpty ||
    isQuantityNonNumeric ||
    isQuantityTooLarge ||
    isDescriptionTooLong ||
    isPnTooLong ||
    isChineseNameTooLong
  )
}

export function filterInvalidRows(data: TrolleySheetData): TrolleySheetData {
  const filtered: TrolleySheetData = {}

  for (const [trolleyName, rows] of Object.entries(data)) {
    if (!rows.length) {
      filtered[trolleyName] = rows
      continue
    }

    const descriptionRow = isDescriptionRow(rows[0]) ? rows[0] : null
    const dataWithoutDescription = getDataRowsWithoutDescription(rows)
    const headerRow = dataWithoutDescription[0]
    const dataRows = dataWithoutDescription.slice(1)

    const filteredRows: ToolRow[] = []
    if (descriptionRow) {
      filteredRows.push(descriptionRow)
    }
    filteredRows.push(headerRow)
    filteredRows.push(...dataRows.filter(row => isValidImportRow(row)))

    filtered[trolleyName] = filteredRows
  }

  return filtered
}
